import React, { useState, useEffect } from 'react';
import { ArrowLeft, User, Mail, Phone, MapPin, Briefcase, Calendar, Save, Lock, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { auth } from '../../config/firebaseConfig';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential, signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/userService';
import AddressAutocomplete from '../common/AddressAutocomplete';
import './styles/MobilePersonalInfoEdit.css';
import { API_BASE_URL } from '../../config/apiConfig';

const MobilePersonalInfoEdit = ({ onBack }) => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    email: '',
    phone: '',
    address: '',
    addressDetail: '',
    addressLat: null,
    addressLng: null,
    addressPlaceId: null,
    occupation: '',
    birthdate: '',
    profileImage: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [hasPassword, setHasPassword] = useState(null); // null = loading
  const isEmailUser = auth.currentUser?.providerData?.some(p => p.providerId === 'password') || hasPassword === false;


  const handleDeleteAccount = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    setIsDeleting(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        await auth.signOut();
        navigate('/auth');
      } else {
        const data = await res.json();
        setMessage({ type: 'error', text: data.message || '계정 삭제에 실패했습니다.' });
        setDeleteConfirm(false);
      }
    } catch (error) {
      setMessage({ type: 'error', text: `오류: ${error.message}` });
      setDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  // 비밀번호 있는 사용자: 현재 비밀번호 확인 후 변경
  const handleChangePassword = async () => {
    if (passwordForm.new !== passwordForm.confirm) {
      setMessage({ type: 'error', text: '새 비밀번호가 일치하지 않습니다.' });
      return;
    }
    if (passwordForm.new.length < 6) {
      setMessage({ type: 'error', text: '비밀번호는 6자 이상이어야 합니다.' });
      return;
    }
    setIsChangingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, passwordForm.current);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, passwordForm.new);
      setPasswordForm({ current: '', new: '', confirm: '' });
      setShowPasswordChange(false);
      setMessage({ type: 'success', text: '비밀번호가 변경되었습니다.' });
    } catch (error) {
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setMessage({ type: 'error', text: '현재 비밀번호가 올바르지 않습니다.' });
      } else {
        setMessage({ type: 'error', text: `비밀번호 변경 실패: ${error.message}` });
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  // 비밀번호 없는 사용자: 서버에서 Firebase + MongoDB 동시 설정
  const handleSetPassword = async () => {
    if (passwordForm.new !== passwordForm.confirm) {
      setMessage({ type: 'error', text: '비밀번호가 일치하지 않습니다.' });
      return;
    }
    if (passwordForm.new.length < 6) {
      setMessage({ type: 'error', text: '비밀번호는 6자 이상이어야 합니다.' });
      return;
    }
    setIsChangingPassword(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ newPassword: passwordForm.new }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || '비밀번호 설정 실패');
      setPasswordForm({ current: '', new: '', confirm: '' });
      setShowPasswordChange(false);
      setHasPassword(true);
      setMessage({ type: 'success', text: '비밀번호가 설정되었습니다.' });
    } catch (error) {
      setMessage({ type: 'error', text: `비밀번호 설정 실패: ${error.message}` });
    } finally {
      setIsChangingPassword(false);
    }
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setIsLoading(true);
        const data = await userService.getUserProfile();
        // 프로필 이미지: localStorage 캐시 우선, 서버 값 대체
        const cachedImage = localStorage.getItem('userProfileImage');
        const profileImage = data.profileImage || cachedImage || '';
        setUserInfo({
          firstName: data.firstName || data.name || '',
          lastName: '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          addressDetail: data.addressDetail || '',
          addressLat: data.addressLat || null,
          addressLng: data.addressLng || null,
          addressPlaceId: data.addressPlaceId || null,
          occupation: data.occupation || '',
          birthdate: data.birthdate ? data.birthdate.split('T')[0] : '',
          profileImage
        });
        setHasPassword(!!data.hasPassword);
        if (profileImage) {
          localStorage.setItem('userProfileImage', profileImage);
        }
      } catch (error) {
        setMessage({ type: 'error', text: '개인정보를 불러오는데 실패했습니다.' });
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserInfo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserInfo(prev => ({ ...prev, [name]: value }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      setMessage({ type: '', text: '' });
      await userService.updateUserProfile(userInfo);
      // 저장 후 localStorage 동기화
      if (userInfo.profileImage) {
        localStorage.setItem('userProfileImage', userInfo.profileImage);
      }
      setMessage({ type: 'success', text: '개인정보가 성공적으로 저장되었습니다!' });
      window.dispatchEvent(new CustomEvent('userProfileUpdated'));
      setTimeout(() => {
        setMessage({ type: '', text: '' });
        onBack();
      }, 1500);
    } catch (error) {
      setMessage({ type: 'error', text: '개인정보 저장에 실패했습니다.' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mobile-personal-info-edit">
        <div className="loading-state">개인정보를 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="mobile-personal-info-edit">
      {/* 헤더 */}
      <div className="mobile-edit-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={24} />
        </button>
        <h1 className="header-title">개인정보 수정</h1>
        <div style={{ width: '40px' }}></div> {/* 레이아웃 균형을 위한 빈 공간 */}
      </div>

      {/* 메시지 */}
      {message.text && (
        <div className={`message-box ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* 폼 */}
      <div className="mobile-edit-content">
        <form onSubmit={handleSubmit} className="edit-form">
          <div className="form-section">
            {/* 사진 + 이름/성 가로 배치 */}
            <div className="form-group" style={{ display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'flex-start' }}>
              {/* 왼쪽: 프로필 사진 (클릭하여 변경) */}
              <label style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <div style={{ position: 'relative', width: '72px', height: '72px' }}>
                  <div style={{
                    width: '72px', height: '72px', borderRadius: '50%',
                    background: '#e5e7eb', overflow: 'hidden', border: '2px solid #d1d5db',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {userInfo.profileImage ? (
                      <img src={userInfo.profileImage} alt="프로필" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <User size={32} color="#9ca3af" />
                    )}
                  </div>
                  {/* 카메라 오버레이 */}
                  <div style={{
                    position: 'absolute', bottom: 0, right: 0,
                    width: '22px', height: '22px', borderRadius: '50%',
                    background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid white'
                  }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="white">
                      <path d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4z"/>
                      <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
                    </svg>
                  </div>
                </div>
                <span style={{ fontSize: '10px', color: '#6b7280' }}>사진 변경</span>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) {
                      setMessage({ type: 'error', text: '이미지는 5MB 이하여야 합니다.' });
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      // canvas로 리사이즈 후 base64 압축 (MongoDB 문서 크기 절감)
                      const img = new Image();
                      img.onload = () => {
                        const MAX = 800;
                        let { width, height } = img;
                        if (width > MAX || height > MAX) {
                          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
                          else { width = Math.round(width * MAX / height); height = MAX; }
                        }
                        const canvas = document.createElement('canvas');
                        canvas.width = width;
                        canvas.height = height;
                        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                        const compressed = canvas.toDataURL('image/jpeg', 0.7);
                        setUserInfo(prev => ({ ...prev, profileImage: compressed }));
                        localStorage.setItem('userProfileImage', compressed);
                      };
                      img.src = ev.target.result;
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
              {/* 오른쪽: 이름 */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#374151' }}>이름</span>
                  <input
                    type="text"
                    name="firstName"
                    value={userInfo.firstName}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="홍길동"
                    style={{ margin: 0 }}
                  />
                </div>
              </div>
            </div>


            <div className="form-group">
              <label className="form-label">
                <Mail size={16} />
                이메일
              </label>
              <input
                type="email"
                name="email"
                value={userInfo.email}
                className="form-input readonly"
                readOnly
              />
              <p className="help-text">이메일은 변경할 수 없습니다.</p>
            </div>

            {/* 비밀번호 설정/변경 (이메일 사용자만) */}
            {isEmailUser && (
              <>
                <button
                  type="button"
                  onClick={() => setShowPasswordChange(!showPasswordChange)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
                    cursor: 'pointer', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: showPasswordChange ? '0' : '0'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Lock size={16} /> {hasPassword ? '비밀번호 변경' : '비밀번호 설정'}
                  </span>
                  {showPasswordChange ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showPasswordChange && (
                  <div style={{ padding: '12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px', marginTop: '-1px' }}>
                    {hasPassword && (
                      <div className="form-group">
                        <label className="form-label"><Lock size={16} /> 현재 비밀번호</label>
                        <input type="password" value={passwordForm.current} onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))} className="form-input" placeholder="현재 비밀번호" />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label"><Lock size={16} /> {hasPassword ? '새 비밀번호' : '비밀번호'}</label>
                      <input type="password" value={passwordForm.new} onChange={(e) => setPasswordForm(prev => ({ ...prev, new: e.target.value }))} className="form-input" placeholder="비밀번호 (6자 이상)" />
                    </div>
                    <div className="form-group">
                      <label className="form-label"><Lock size={16} /> {hasPassword ? '새 비밀번호 확인' : '비밀번호 확인'}</label>
                      <input type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))} className="form-input" placeholder="비밀번호 확인" />
                    </div>
                    <button
                      type="button"
                      onClick={hasPassword ? handleChangePassword : handleSetPassword}
                      disabled={isChangingPassword || (hasPassword && !passwordForm.current) || !passwordForm.new || !passwordForm.confirm}
                      className="submit-button"
                      style={{ background: isChangingPassword ? '#9ca3af' : '#3b82f6' }}
                    >
                      {isChangingPassword ? (hasPassword ? '변경 중...' : '설정 중...') : (hasPassword ? '비밀번호 변경' : '비밀번호 설정')}
                    </button>
                  </div>
                )}
              </>
            )}

            <div className="form-group">
              <label className="form-label">
                <Phone size={16} />
                전화번호
              </label>
              <input
                type="tel"
                name="phone"
                value={userInfo.phone}
                onChange={handleChange}
                className="form-input"
                placeholder="010-1234-5678"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <MapPin size={16} />
                주소
              </label>
              <AddressAutocomplete 
                value={userInfo.address} 
                onChange={(data) => {
                  setUserInfo(p => ({
                    ...p, 
                    address: data.address,
                    addressLat: data.lat,
                    addressLng: data.lng,
                    addressPlaceId: data.placeId
                  }));
                }} 
              />
              {userInfo.addressLat && (
                <p className="help-text" style={{ color: '#10b981' }}>
                  ✓ 위치 좌표: {userInfo.addressLat.toFixed(6)}, {userInfo.addressLng.toFixed(6)}
                </p>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <MapPin size={16} />
                세부 주소
              </label>
              <input
                type="text"
                name="addressDetail"
                value={userInfo.addressDetail}
                onChange={handleChange}
                className="form-input"
                placeholder="상세 주소 (예: 101동 1004호)"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Briefcase size={16} />
                직업
              </label>
              <input
                type="text"
                name="occupation"
                value={userInfo.occupation}
                onChange={handleChange}
                className="form-input"
                placeholder="직업"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Calendar size={16} />
                생년월일
              </label>
              <input
                type="date"
                name="birthdate"
                value={userInfo.birthdate}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={isSaving}
              className="submit-button"
            >
              {isSaving ? '저장 중...' : '저장하기'}
            </button>
          </div>
        </form>

        {/* 지도 표시 */}
        <div className="map-section">
          <h3 className="map-title">위치 지도</h3>
          {userInfo.addressLat && userInfo.addressLng ? (
            <div className="map-container">
              <iframe
                title="위치 지도"
                width="100%"
                height="300"
                style={{ border: 0, display: 'block' }}
                loading="lazy"
                src={`https://www.google.com/maps?q=${userInfo.addressLat},${userInfo.addressLng}&z=15&output=embed`}
              />
            </div>
          ) : (
            <div className="map-placeholder">
              <MapPin size={48} className="placeholder-icon" />
              <p className="placeholder-text">주소를 입력하면 지도가 표시됩니다</p>
              <p className="placeholder-subtext">
                현재 주소: {userInfo.address || '없음'}
              </p>
            </div>
          )}
        </div>

        {/* 계정 탈퇴 */}
        <div style={{ margin: '24px 0 8px 0', padding: '0 16px' }}>
          <div style={{
            background: 'white', borderRadius: '12px',
            border: '1px solid #fecaca', overflow: 'hidden',
          }}>
            <div style={{ padding: '16px' }}>
              {deleteConfirm ? (
                <div>
                  <p style={{ fontSize: '13px', color: '#dc2626', marginBottom: '12px', lineHeight: 1.5 }}>
                    정말 탈퇴하시겠습니까? 모든 데이터가 <strong>영구 삭제</strong>되며 복구할 수 없습니다.
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setDeleteConfirm(false)}
                      style={{ flex: 1, padding: '10px', fontSize: '14px', fontWeight: 600, color: '#374151', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                    >
                      취소
                    </button>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={isDeleting}
                      style={{ flex: 1, padding: '10px', fontSize: '14px', fontWeight: 600, color: 'white', background: '#dc2626', border: 'none', borderRadius: '8px', cursor: isDeleting ? 'default' : 'pointer' }}
                    >
                      {isDeleting ? '처리 중...' : '탈퇴 확인'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleDeleteAccount}
                  style={{
                    width: '100%', padding: '10px', fontSize: '14px', fontWeight: 600,
                    color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca',
                    borderRadius: '8px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  }}
                >
                  <Trash2 size={16} /> 계정 탈퇴
                </button>
              )}
              <p style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', margin: '8px 0 0 0' }}>
                탈퇴 시 모든 데이터가 삭제되며 복구할 수 없습니다.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobilePersonalInfoEdit;
